import React from "react"
import { getBrandStateLabel } from "../../../../utils/brandStateLabels"

interface BrandStateHistoryItem {
  _id: string
  brand: string
  state: string
  createdAt: string
  updatedAt: string
  application_done: number
  application_received: number
  has_applications: boolean
  has_campaigns: boolean
  has_draft_campaigns: boolean
  has_multiple_balance_loads: boolean
  profile_approved: number
  __v: number
}

interface BrandStateHistoryModalProps {
  isOpen: boolean
  onClose: () => void
  brandId: string
  brandName: string
  brandStateHistory: BrandStateHistoryItem[]
}

const BrandStateHistoryModal: React.FC<BrandStateHistoryModalProps> = ({
  isOpen,
  onClose,
  brandId: _brandId,
  brandName,
  brandStateHistory,
}) => {
  const calculateDuration = (startDate: string, endDate: string) => {
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    const diffHours = Math.ceil(diffTime / (1000 * 60 * 60))
    const diffMinutes = Math.ceil(diffTime / (1000 * 60))

    if (diffDays > 1) {
      return `${diffDays} gün`
    } else if (diffHours > 1) {
      return `${diffHours} saat`
    } else {
      return `${diffMinutes} dakika`
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="fixed right-0 top-0 h-full z-50">
      <div className="bg-white h-full w-96 shadow-lg flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-semibold">Marka Durum Geçmişi</h2>
              <p className="text-sm opacity-90 mt-1">{brandName}</p>
            </div>
            <button onClick={onClose} className="text-white hover:text-gray-200">
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {brandStateHistory && brandStateHistory.length > 0 ? (
            <div className="space-y-3">
              {brandStateHistory.map((historyItem, index) => {
                const nextItem = brandStateHistory[index + 1]
                const duration = nextItem
                  ? calculateDuration(historyItem.createdAt, nextItem.createdAt)
                  : "Devam ediyor"

                return (
                  <div
                    key={historyItem._id}
                    className="bg-gray-50 border border-gray-200 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {index + 1}
                      </span>
                      <span className="text-xs text-gray-500">{duration}</span>
                    </div>
                    <h3 className="font-medium text-sm text-gray-800 mb-1">
                      {getBrandStateLabel(historyItem.state)}
                    </h3>
                    <p className="text-xs text-gray-600">{formatDate(historyItem.createdAt)}</p>
                    <div className="mt-2 text-xs text-gray-500">
                      <div className="grid grid-cols-2 gap-1">
                        <span>Başvuru: {historyItem.application_done}</span>
                        <span>Alınan: {historyItem.application_received}</span>
                        <span>Onaylanan: {historyItem.profile_approved}</span>
                        <span>Kampanya: {historyItem.has_campaigns ? "Var" : "Yok"}</span>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>Durum geçmişi bulunamadı</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Toplam: {brandStateHistory ? brandStateHistory.length : 0} durum geçmişi
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrandStateHistoryModal
